import React, { useState, useContext, useCallback, useMemo, useRef, useEffect } from "react";
import { format } from "date-fns";
import { Redirect, useHistory } from "react-router-dom";
import {
  IonContent,
  IonPage,
  IonRow,
  IonCol,
  IonLabel,
  IonItem,
  IonAvatar,
  IonList,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonIcon,
  IonBadge,
  useIonAlert,
  IonButton,
  IonSegment,
  IonSegmentButton,
  IonCard,
  IonCardContent,
  IonText,
  useIonActionSheet,
  useIonViewWillLeave,
} from "@ionic/react";
import ChatListItem from "../components/ChatListItem";
import "../theme/AppStyles.css";
import { add, createOutline, chevronUpOutline, chatbox, chatboxOutline } from "ionicons/icons";
import { ProfileContext } from "../context/ProfileContext";
import { AuthContext } from "../context/AuthContext";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import TabNavigator from "../components/TabNavigator";
import { LoadingScreen } from "../components/LoadingScreen";

import { chatFunction } from "../functions/Fx_Chats";
import { SUPPORT_MONKEY_ASSOCIATE, SUPPORT_MONKEY_UID } from "../constants/supportConstants";
import { isDesktop } from "../utils/platformUtils";
import ChatsDesktop from "./desktop/ChatsDesktop";
import { useChatListFilter } from "../hooks/useChatListFilter";
import { useAnnouncementLists } from "../hooks/useAnnouncementLists";
import { softDeleteAnnouncement } from "../domain/announcements/announcementActions";

const Chats: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const {
    myChats,
    myAnnouncements,
    profile,
    myAssociates,
    myAnnouncementPosts,
    allowedJobIdsWerker,
    allowedJobIdsScheduler,
    allowedJobIdsUnion,
    jobFilteringReady,
    jobRouteByJobId,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  } = useContext(ProfileContext);

  const [commSegment, setCommSegment] = useState<"announcements" | "chats">("announcements");
  const [announcementView, setAnnouncementView] = useState<"feed" | "mine">("feed");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const showScrollTopRef = useRef(false);
  const scrollRafPendingRef = useRef(false);

  const contentRef = useRef<HTMLIonContentElement>(null);

  const history = useHistory<any>();
  const [presentAlert] = useIonAlert();
  const [presentActionSheet] = useIonActionSheet();

  const FALLBACK_AVATAR = "../assets/profilePic.png";

  const isSchedulerMode = profile?.UserLastRoleScheduler === true;

  // Use shared chat list filter hook
  const { filteredChats, searchText, setSearchText } = useChatListFilter({
    myChats,
    myAssociates,
    jobFilteringReady,
    allowedJobIdsUnion,
    cancelledBySchedulerWerker,
    cancelledBySchedulerScheduler,
  });

  // Debounce searchbar input so the heavy filter+sort+map only runs after typing pauses.
  const [searchInput, setSearchInput] = useState(searchText);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchText(searchInput);
    }, 250);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput, setSearchText]);

  const supportUnread = useMemo(() => {
    const row = (myChats || []).find((m: any) => String(m?.DMuid ?? "") === SUPPORT_MONKEY_UID);
    if (!row) return 0;
    return row?.IsRead === false ? 1 : 0;
  }, [myChats]);

  const blurFocusedElement = useCallback(() => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      activeElement.blur();
    }
  }, []);

  useIonViewWillLeave(() => {
    blurFocusedElement();
  });

  // Helper for navigation logic
  const isJobChat = useCallback((msg: any) => {
    return typeof msg?.DMuid === "string" && msg.DMuid.trim() === "";
  }, []);

  // -----------------------------
  // Navigation
  // -----------------------------
  const createChat = useCallback(() => {
    blurFocusedElement();
    history.push({ pathname: `/CreateNewChat` });
  }, [history, blurFocusedElement]);

  const createAnnouncement = useCallback(() => {
    blurFocusedElement();
    history.push({
      pathname: "/Announcement",
      state: {
        cameFrom: "Chats",
      },
    });
  }, [history, blurFocusedElement]);

  const openAnnouncementDetail = useCallback(
    (announcementId: string) => {
      blurFocusedElement();
      history.push({
        pathname: `/AnnouncementDetail/${announcementId}`,
        state: {
          cameFrom: "Chats",
        },
      });
    },
    [history, blurFocusedElement]
  );

  const handleRowClick = useCallback(
    (msg: any) => {
      blurFocusedElement();
      const key = String(msg?.Key || "");
      if (!key) return;

      if (isJobChat(msg)) {
        const jobId = key;

        const hasSchedView = allowedJobIdsScheduler.has(jobId);
        const hasWerkerView = allowedJobIdsWerker.has(jobId);

        const goChat = () => {
          history.push({
            pathname: `/Chat/${jobId}`,
            state: { cameFrom: "Chats" },
          });
        };

        const goSched = () => {
          history.push({
            pathname: `/SchedShiftDetail/${jobId}`,
            state: { cameFrom: "Chats" },
          });
        };

        const goWerker = () => {
          const meta = jobRouteByJobId[jobId];
          if (meta?.myJobDocId && meta?.schedUid) {
            history.push({
              pathname: `/ShiftDetails/${meta.myJobDocId}/${jobId}/${meta.schedUid}`,
              state: { cameFrom: "Chats" },
            });
            return;
          }
          goChat();
        };

        if (profile?.UserLastRoleScheduler === true) {
          if (hasSchedView) return goSched();
          if (hasWerkerView) return goWerker();
          return goChat();
        } else {
          if (hasWerkerView) return goWerker();
          if (hasSchedView) return goSched();
          return goChat();
        }
      }

      history.push({
        pathname: `/Chat/${key}`,
        state: { cameFrom: "Chats" },
      });
    },
    [
      history,
      blurFocusedElement,
      isJobChat,
      allowedJobIdsScheduler,
      allowedJobIdsWerker,
      jobRouteByJobId,
      profile?.UserLastRoleScheduler,
    ]
  );

  const { currentAnnouncementList, unreadAnnouncementsCount } = useAnnouncementLists({
    myAnnouncements,
    myAnnouncementPosts,
    searchText,
    announcementView,
  });

  const unreadChatsCount = useMemo(() => {
    return (filteredChats || []).filter((msg: any) => msg?.IsRead === false).length;
  }, [filteredChats]);

  const deleteAnnouncement = useCallback(
    async (announcementId: string) => {
      presentAlert({
        header: "Delete Announcement",
        message: "Are you sure you want to delete this announcement?",
        buttons: [
          { text: "Cancel", role: "cancel" },
          {
            text: "Delete",
            role: "destructive",
            handler: async () => {
              try {
                await softDeleteAnnouncement(announcementId);
              } catch (error) {
                // console.log("deleteAnnouncement error:", error);
              }
            },
          },
        ],
      });
    },
    [presentAlert]
  );

  const _openAnnouncementMenu = useCallback(
    (item: any) => {
      presentActionSheet({
        header: item?.title || "Announcement",
        buttons: [
          {
            text: "Edit",
            icon: createOutline,
            handler: () => {
              history.push({
                pathname: "/Announcement",
                state: {
                  cameFrom: "AnnouncementDetail",
                  mode: "edit",
                  announcementId: item.announcementId,
                  recipientUids: Array.isArray(item.recipientUids) ? item.recipientUids : [],
                  selectedCrewIds: Array.isArray(item.selectedCrewIds) ? item.selectedCrewIds : [],
                  draftTitle: item.title || "",
                  draftBody: item.body || "",
                  draftImageUrl: item.imageUrl || "",
                },
              });
            },
          },
          {
            text: "Delete",
            role: "destructive",
            handler: () => {
              deleteAnnouncement(item.announcementId);
            },
          },
          {
            text: "Cancel",
            role: "cancel",
          },
        ],
      });
    },
    [presentActionSheet, history, deleteAnnouncement]
  );

  const chatsReady = useMemo(() => Array.isArray(myChats), [myChats]);
  const announcementsReady = useMemo(() => Array.isArray(myAnnouncements), [myAnnouncements]);

  // -----------------------------
  // FABs
  // -----------------------------
  const openSupportChat = useCallback(() => {
    presentAlert({
      header: "Customer Support",
      message: "Chat with WerkApp Customer Support Monkeys",
      buttons: [
        { text: "Cancel", role: "cancel" },
        {
          text: "Chat",
          handler: async () => {
            try {
              const result = await chatFunction(profile, SUPPORT_MONKEY_ASSOCIATE, "DM");
              history.push({
                pathname: `/Chat/${result}`,
                state: { cameFrom: "Chats" },
              });
            } catch (error) {
              console.error("Error opening support chat:", error);
            }
          },
        },
      ],
    });
  }, [presentAlert, profile, history]);

  const monkeyChatFab = useMemo(
    () => (
      <IonFab slot="fixed" horizontal="end" vertical="bottom" style={{ bottom: "30px" }}>
        <IonFabButton
          color={supportUnread > 0 ? "warning" : "medium"}
          onClick={openSupportChat}
          className="chatMonkeyFab"
        >
          <div className="chatMonkeyFabImgWrap">
            <img src={SUPPORT_MONKEY_ASSOCIATE.WerkerProfilePicURL} alt="Support" className="chatMonkeyFabImg" />

            {supportUnread > 0 && (
              <IonBadge color="danger" className="chatMonkeyFabBadge">
                {supportUnread}
              </IonBadge>
            )}
          </div>
        </IonFabButton>
      </IonFab>
    ),
    [supportUnread, openSupportChat]
  );

  // -----------------------------
  // Render guards
  // -----------------------------
  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to="login" />;
  }

  // Desktop mode - render desktop version
  if (isDesktop()) {
    return <ChatsDesktop />;
  }

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <IonPage>
      <WerkerSchedulerHeader title={"Communications"} />

      <div style={{ paddingTop: 10, paddingBottom: 4 }}>
        <IonSegment
          value={commSegment}
          onIonChange={(e) => setCommSegment((e.detail.value as "announcements" | "chats") || "announcements")}
        >
          <IonSegmentButton value="announcements">
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <IonLabel>Announcements</IonLabel>
              {unreadAnnouncementsCount > 0 && (
                <IonBadge color="danger" className="chatSegmentBadge">
                  {unreadAnnouncementsCount}
                </IonBadge>
              )}
            </div>
          </IonSegmentButton>

          <IonSegmentButton value="chats">
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <IonLabel>Chats</IonLabel>
              <IonIcon icon={chatboxOutline} style={{ fontSize: "16px" }} />
              {unreadChatsCount > 0 && (
                <IonBadge color="danger" className="chatSegmentBadge">
                  {unreadChatsCount}
                </IonBadge>
              )}
            </div>
          </IonSegmentButton>
        </IonSegment>
      </div>

      <IonSearchbar
        value={searchInput}
        onIonInput={(e) => setSearchInput((e.target as HTMLIonSearchbarElement).value || "")}
        animated
        placeholder="Search"
      />
      <IonContent
        ref={contentRef}
        scrollEvents={true}
        onIonScroll={(e) => {
          const scrollTop = e.detail.scrollTop;
          if (scrollRafPendingRef.current) return;
          scrollRafPendingRef.current = true;
          requestAnimationFrame(() => {
            scrollRafPendingRef.current = false;
            const shouldShow = scrollTop > 400;
            if (shouldShow !== showScrollTopRef.current) {
              showScrollTopRef.current = shouldShow;
              setShowScrollTop(shouldShow);
            }
          });
        }}
      >
        {commSegment === "chats" && (
          <>
            <IonItem style={{ marginBottom: "15px" }} lines="none">
              <IonButton
                size="small"
                slot="end"
                color={profile?.UserLastRoleScheduler ? "secondarywarning" : "warning"}
                expand="block"
                onClick={createChat}
              >
                New Chat
                <IonIcon icon={add} slot="start" />
              </IonButton>
            </IonItem>

            {(!chatsReady || !jobFilteringReady) && (
              <IonRow>
                <IonCol size="1" />
                <IonCol>
                  <h2>Loading chats...</h2>
                </IonCol>
                <IonCol size="1" />
              </IonRow>
            )}

            {chatsReady && jobFilteringReady && filteredChats.length === 0 && (
              <IonRow>
                <IonCol size="1" />
                <IonCol>
                  <h2>You have no messages</h2>
                </IonCol>
                <IonCol size="1" />
              </IonRow>
            )}

            {chatsReady && jobFilteringReady && filteredChats.length > 0 && (
              <IonList>
                {filteredChats.map((msg: any) => (
                  <ChatListItem
                    key={msg.Key}
                    msg={msg}
                    isScheduler={isSchedulerMode}
                    fallbackAvatar={FALLBACK_AVATAR}
                    onSelect={handleRowClick}
                  />
                ))}
              </IonList>
            )}

            {monkeyChatFab}
          </>
        )}

        {commSegment === "announcements" && (
          <>
            {isSchedulerMode && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 12px 4px 12px",
                }}
              >
                <IonSegment
                  value={announcementView}
                  onIonChange={(e) => setAnnouncementView((e.detail.value as "feed" | "mine") || "feed")}
                  style={{
                    maxWidth: "180px",
                    minWidth: "150px",
                  }}
                >
                  <IonSegmentButton value="feed">
                    <IonLabel>Feed</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="mine">
                    <IonLabel>Sent</IonLabel>
                  </IonSegmentButton>
                </IonSegment>

                <IonButton
                  size="small"
                  color={profile?.UserLastRoleScheduler ? "secondarywarning" : "warning"}
                  onClick={createAnnouncement}
                >
                  New
                  <IonIcon icon={add} slot="start" />
                </IonButton>
              </div>
            )}

            {!announcementsReady && (
              <IonRow>
                <IonCol size="1" />
                <IonCol>
                  <h2>Loading announcements...</h2>
                </IonCol>
                <IonCol size="1" />
              </IonRow>
            )}

            {announcementsReady && currentAnnouncementList.length === 0 && (
              <IonRow>
                <IonCol size="1" />
                <IonCol>
                  <h2>
                    {announcementView === "feed" ? "No announcements yet" : "You have not posted any announcements"}
                  </h2>
                </IonCol>
                <IonCol size="1" />
              </IonRow>
            )}

            {announcementsReady && currentAnnouncementList.length > 0 && (
              <IonList>
                {currentAnnouncementList.map((item: any) => {
                  const unread = item?.isRead === false;
                  const dateMs = Number(item?.createdAt) || 0;

                  const previewText =
                    announcementView === "mine"
                      ? String(item?.body || "").trim()
                      : String(item?.bodyPreview || "").trim();

                  const canManageFromList = announcementView === "mine" && isSchedulerMode;

                  return (
                    <div
                      key={item.announcementId}
                      style={{
                        margin: "10px 12px",
                        borderLeft: unread
                          ? `4px solid var(--ion-color-${profile?.UserLastRoleScheduler ? "secondwarning" : "warning"})`
                          : "4px solid transparent",
                        borderRadius: "12px",
                        paddingLeft: unread ? "0px" : "0px",
                      }}
                    >
                      <IonCard
                        button={true}
                        onClick={() => openAnnouncementDetail(item.announcementId)}
                        style={{
                          margin: 0,
                        }}
                      >
                        <IonCardContent>
                          {/* HEADER */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 12,
                              marginBottom: canManageFromList ? 8 : 10,
                            }}
                          >
                            {/* LEFT SIDE */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <IonAvatar
                                style={{
                                  width: 36,
                                  height: 36,
                                  flexShrink: 0,
                                  marginTop: 1,
                                }}
                              >
                                <img
                                  src={item?.authorProfilePicURL || "../assets/profilePic.png"}
                                  alt=""
                                  loading="lazy"
                                  decoding="async"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => {
                                    const el = e.currentTarget;
                                    if (el.src.includes("../assets/profilePic.png")) return;
                                    el.src = "../assets/profilePic.png";
                                  }}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </IonAvatar>

                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <IonText>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontWeight: 700,
                                      lineHeight: 1.2,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={item.authorName || ""}
                                  >
                                    {item.authorName || "Unknown Author"}
                                  </p>
                                </IonText>

                                <IonText color="medium">
                                  <p
                                    style={{
                                      margin: "2px 0 0 0",
                                      fontSize: "0.85rem",
                                      lineHeight: 1.2,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={item.authorCompany || ""}
                                  >
                                    {item.authorCompany || ""}
                                  </p>
                                </IonText>
                              </div>
                            </div>

                            {/* RIGHT SIDE */}
                            <div
                              style={{
                                flexShrink: 0,
                                textAlign: "right",
                                marginLeft: 8,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                minWidth: "80px",
                              }}
                            >
                              <IonText color="medium">
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "0.78rem",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {!!dateMs ? format(new Date(dateMs), "M/d h:mm a") : ""}
                                </p>
                              </IonText>
                            </div>
                          </div>

                          {/* TITLE */}
                          <IonText>
                            <h2
                              style={{
                                margin: "8px 0 10px 0",
                                fontSize: "1.08rem",
                                lineHeight: 1.3,
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              {item.title || ""}
                            </h2>
                          </IonText>

                          {/* BODY PREVIEW */}
                          <IonText color="dark">
                            <p
                              style={{
                                marginTop: 0,
                                marginBottom: 10,
                                whiteSpace: "pre-wrap",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.45,
                              }}
                            >
                              {previewText}
                            </p>
                          </IonText>

                          {/* IMAGE */}
                          {!!item.imageUrl && (
                            <div style={{ marginTop: 8 }}>
                              <img
                                src={item.imageUrl}
                                alt="Announcement"
                                style={{
                                  width: "100%",
                                  maxHeight: "220px",
                                  objectFit: "cover",
                                  borderRadius: "12px",
                                }}
                                onError={(e) => {
                                  const el = e.currentTarget as HTMLImageElement;
                                  el.style.display = "none";
                                }}
                              />
                            </div>
                          )}
                        </IonCardContent>
                      </IonCard>
                    </div>
                  );
                })}
              </IonList>
            )}
          </>
        )}
        {commSegment === "announcements" && (
          <IonFab
            slot="fixed"
            horizontal="end"
            vertical="bottom"
            style={{
              bottom: "30px",
              opacity: showScrollTop ? 1 : 0,
              pointerEvents: showScrollTop ? "auto" : "none",
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            <IonFabButton
              size="small"
              color="medium"
              onClick={() => contentRef.current?.scrollToTop(300)}
              style={{ "--box-shadow": "0 2px 12px rgba(0,0,0,0.15)", opacity: 0.85 }}
            >
              <IonIcon icon={chevronUpOutline} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>

      <TabNavigator />
    </IonPage>
  );
};

export default Chats;
